import React from 'react';

const SkeletonPulse = ({ className = "", isDarkMode = false }) => (
  <div className={`${className} animate-pulse ${isDarkMode ? 'bg-slate-800' : 'bg-slate-200'}`} />
);

export const SummarySkeleton = ({ isDarkMode = false }) => (
  <div className="space-y-6 pb-20">
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className={`p-6 rounded-[2rem] border ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'}`}>
          <SkeletonPulse className="h-4 w-24 mb-3 rounded" isDarkMode={isDarkMode} />
          <SkeletonPulse className="h-8 w-32 rounded" isDarkMode={isDarkMode} />
        </div>
      ))}
    </div>
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className={`col-span-1 lg:col-span-2 p-6 rounded-[2rem] border h-80 ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'}`}>
        <SkeletonPulse className="h-6 w-40 mb-4 rounded" isDarkMode={isDarkMode} />
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => <SkeletonPulse key={i} className="h-12 w-full rounded" isDarkMode={isDarkMode} />)}
        </div>
      </div>
      <div className={`p-6 rounded-[2rem] border h-80 ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'}`}>
        <SkeletonPulse className="h-6 w-32 mb-4 rounded" isDarkMode={isDarkMode} />
        <SkeletonPulse className="h-64 w-full rounded" isDarkMode={isDarkMode} />
      </div>
    </div>
  </div>
);

export const HoldingSkeleton = ({ isDarkMode = false }) => (
  <div className="space-y-6 pb-20">
    <SkeletonPulse className="h-8 w-48 rounded mb-4" isDarkMode={isDarkMode} />
    <div className="space-y-3">
      {[...Array(8)].map((_, i) => (
        <div key={i} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'} flex gap-4`}>
          <SkeletonPulse className="h-10 w-16 rounded" isDarkMode={isDarkMode} />
          <SkeletonPulse className="h-10 flex-1 rounded" isDarkMode={isDarkMode} />
          <SkeletonPulse className="h-10 w-24 rounded" isDarkMode={isDarkMode} />
          <SkeletonPulse className="h-10 w-24 rounded" isDarkMode={isDarkMode} />
        </div>
      ))}
    </div>
  </div>
);

export const TransactionSkeleton = ({ isDarkMode = false }) => (
  <div className="space-y-6 pb-20">
    <div className="flex gap-3 mb-6">
      <SkeletonPulse className="h-10 w-32 rounded" isDarkMode={isDarkMode} />
      <SkeletonPulse className="h-10 w-32 rounded" isDarkMode={isDarkMode} />
    </div>
    <div className="space-y-3">
      {[...Array(10)].map((_, i) => (
        <div key={i} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'} grid grid-cols-6 gap-3`}>
          {[...Array(6)].map((_, j) => <SkeletonPulse key={j} className="h-8 rounded" isDarkMode={isDarkMode} />)}
        </div>
      ))}
    </div>
  </div>
);

export const DividendSkeleton = ({ isDarkMode = false }) => (
  <div className="space-y-6 pb-20">
    <div className="flex gap-3 mb-6">
      <SkeletonPulse className="h-10 w-32 rounded" isDarkMode={isDarkMode} />
      <SkeletonPulse className="h-10 w-32 rounded" isDarkMode={isDarkMode} />
    </div>
    <div className="space-y-3">
      {[...Array(6)].map((_, i) => (
        <div key={i} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'} grid grid-cols-5 gap-3`}>
          {[...Array(5)].map((_, j) => <SkeletonPulse key={j} className="h-8 rounded" isDarkMode={isDarkMode} />)}
        </div>
      ))}
    </div>
  </div>
);

export const CapitalSkeleton = ({ isDarkMode = false }) => (
  <div className="space-y-6 pb-20">
    <div className="flex gap-3 mb-6">
      <SkeletonPulse className="h-10 w-32 rounded" isDarkMode={isDarkMode} />
    </div>
    <div className="space-y-3">
      {[...Array(5)].map((_, i) => (
        <div key={i} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'} grid grid-cols-4 gap-3`}>
          {[...Array(4)].map((_, j) => <SkeletonPulse key={j} className="h-8 rounded" isDarkMode={isDarkMode} />)}
        </div>
      ))}
    </div>
  </div>
);

export const XDSkeleton = ({ isDarkMode = false }) => (
  <div className="space-y-6 pb-20">
    <SkeletonPulse className="h-8 w-48 rounded mb-6" isDarkMode={isDarkMode} />
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
      {[...Array(12)].map((_, i) => (
        <div key={i} className={`p-5 rounded-[2rem] border h-40 ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'}`}>
          <SkeletonPulse className="h-6 w-24 mb-4 rounded" isDarkMode={isDarkMode} />
          <div className="space-y-2">
            <SkeletonPulse className="h-8 w-full rounded" isDarkMode={isDarkMode} />
            <SkeletonPulse className="h-8 w-3/4 rounded" isDarkMode={isDarkMode} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const InsightsSkeleton = ({ isDarkMode = false }) => (
  <div className="space-y-6 pb-20">
    <SkeletonPulse className="h-8 w-48 rounded mb-6" isDarkMode={isDarkMode} />
    <div className={`p-6 rounded-[2rem] border h-96 ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'}`}>
      <SkeletonPulse className="h-full w-full rounded" isDarkMode={isDarkMode} />
    </div>
  </div>
);

export const RealizedPLSkeleton = ({ isDarkMode = false }) => (
  <div className="space-y-6 pb-20">
    <SkeletonPulse className="h-8 w-48 rounded mb-6" isDarkMode={isDarkMode} />
    <div className="space-y-3">
      {[...Array(8)].map((_, i) => (
        <div key={i} className={`p-4 rounded-lg border ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'} grid grid-cols-6 gap-3`}>
          {[...Array(6)].map((_, j) => <SkeletonPulse key={j} className="h-8 rounded" isDarkMode={isDarkMode} />)}
        </div>
      ))}
    </div>
  </div>
);

export const FundamentalSkeleton = ({ isDarkMode = false }) => (
  <div className="space-y-6 pb-20">
    <SkeletonPulse className="h-8 w-48 rounded mb-6" isDarkMode={isDarkMode} />
    <div className={`p-6 rounded-[2rem] border h-96 ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'}`}>
      <SkeletonPulse className="h-full w-full rounded" isDarkMode={isDarkMode} />
    </div>
  </div>
);

export const AllocationSkeleton = ({ isDarkMode = false }) => (
  <div className="space-y-6 pb-20">
    <SkeletonPulse className="h-8 w-48 rounded mb-6" isDarkMode={isDarkMode} />
    <div className={`p-6 rounded-[2rem] border h-96 ${isDarkMode ? 'bg-[#1E1F20] border-slate-700' : 'bg-white border-slate-200'}`}>
      <SkeletonPulse className="h-full w-full rounded" isDarkMode={isDarkMode} />
    </div>
  </div>
);

export const AdminSkeleton = ({ isDarkMode = false, subTab = "approvals" }) => {
  const rows = subTab === "members" ? 8 : subTab === "articles" ? 4 : 5;
  const cols = subTab === "members" ? 6 : subTab === "approvals" ? 5 : 4;
  return (
    <div className="space-y-4 pb-20">
      <div className="flex gap-2 pb-2 border-b border-slate-200 dark:border-slate-800">
        {[...Array(6)].map((_, i) => (
          <SkeletonPulse key={i} className="h-8 w-24 rounded-xl" isDarkMode={isDarkMode} />
        ))}
      </div>
      <div className={`rounded-[2rem] border overflow-hidden ${isDarkMode ? 'bg-[#1E1F20] border-slate-800' : 'bg-white border-slate-200'}`}>
        <div className={`grid gap-3 p-4 border-b ${isDarkMode ? 'border-slate-800 bg-slate-900/50' : 'border-slate-100 bg-slate-50'}`} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {[...Array(cols)].map((_, i) => <SkeletonPulse key={i} className="h-4 rounded" isDarkMode={isDarkMode} />)}
        </div>
        <div className={`divide-y ${isDarkMode ? 'divide-slate-800' : 'divide-slate-100'}`}>
          {[...Array(rows)].map((_, i) => (
            <div key={i} className="grid gap-3 p-4" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
              {[...Array(cols)].map((_, j) => <SkeletonPulse key={j} className="h-8 rounded" isDarkMode={isDarkMode} />)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};